import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { StyledPaper, StyledGrid } from "@hcl-commerce-store-sdk/react-component";
import { StyledHeaderFooterLink, StyledTypography, StyledBox, StyledButton } from "../../../../../styled-mui";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "react-i18next";
import AsyncCall from "../../../../../_foundation/gtm/async.service";
import { CLICK } from "../../../../../_foundation/constants/gtm";
import { HUBPROMO, MODELSLIST_BIS, PRISEDERDV, SERVICES_ATELIER } from "../../../../../constants/routes";
import { useSite } from "../../../../../_foundation/hooks/useSite";
import SmartLink from "../../../../elements/smart-link/smart-link";
import { palette } from "../../../../../themes/color-palette";
import { HeaderMegaMenuSX } from "../../../headerMegaMenu";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { GetTopCategorySelector } from "../../../../../redux/selectors/category";
import { Grid } from "@mui/material";
import { BlueCard, RedCard } from "../DesktopActionCards";
import { ImageLayout } from "../../../../elements/image";
import storeUtil from "../../../../../utils/storeUtil";
import { DiscountTagIcon } from "../../../NavgationBarSvg";

interface DesktopProductsMenuProps {
  closeHeaderMenu?: () => any;
  menuLinksData: Record<string, any>;
}

const DesktopProductsMenu: React.FC<DesktopProductsMenuProps> = ({ closeHeaderMenu, menuLinksData }) => {
  const { mySite } = useSite();
  const { t } = useTranslation();
  const categories = useSelector(GetTopCategorySelector);
  const [activeMenuId, setActiveMenuId] = useState({ child0: 0, child1: 0 });
  const [catalogueLinks, setCatalogueLinks] = useState<{ label: string; url: string; target: string; type: string }[]>(
    []
  );
  const activeCategory = categories[activeMenuId.child0];

  useEffect(() => {
    // Ensure required data is present
    if (!categories?.length || !menuLinksData?.otherMenuLinks?.length) {
      return;
    }
    const activeCategory = categories[activeMenuId.child0];
    // If no active category identifier, reset and return
    if (!activeCategory?.identifier) {
      setCatalogueLinks([]);
      return;
    }
    // Check if the identifier exists in any of the menuLinksData categories
    const isIdentifierPresent = menuLinksData.otherMenuLinks.some((item) =>
      item.categories?.includes(activeCategory.identifier)
    );
    if (isIdentifierPresent) {
      // Filter links to only show those that match the active category's identifier
      const filteredLinks = menuLinksData.otherMenuLinks
        .filter((item) => item.categories?.includes(activeCategory.identifier))
        .flatMap(
          (item) =>
            item.links?.map((link) => ({
              label: link.label,
              url: link.url,
              target: link.target,
              type: link.type,
            })) || []
        );

      setCatalogueLinks(filteredLinks);
    } else {
      setCatalogueLinks([]);
    }
  }, [categories, menuLinksData, activeMenuId]);

  const Links = ({ page }) => {
    let finalSeoHref = "";
    if (
      (!mySite.storeCfg.userData.SEO_FE_DISABLE ||
        (mySite.storeCfg.userData.SEO_FE_DISABLE && mySite.storeCfg.userData.SEO_FE_DISABLE !== "true")) &&
      page?.seo?.href?.split("/").length === 2
    ) {
      const seoHref = page?.seo.href;

      finalSeoHref = `/produits${categories[activeMenuId.child0].seo?.href}${
        categories[activeMenuId.child0]?.children[activeMenuId.child1].seo?.href
      }${seoHref}`;
    } else {
      finalSeoHref = page?.seo?.href;
    }
    return (
      <StyledHeaderFooterLink
        testId={`header-${page.id}`}
        key={"Link_" + page.id}
        onClick={() => {
          AsyncCall.sendClick(CLICK.NAV, { navlink: finalSeoHref });
          closeHeaderMenu && closeHeaderMenu();
        }}
        to={finalSeoHref}
        state={{
          breadCrumbTrailEntryView: [
            { label: page.name, value: page.id, seo: page.seo },
            {
              label: page.name,
              value: page.id,
              seo: page.seo,
            },
          ],
        }}>
        {page.name}
      </StyledHeaderFooterLink>
    );
  };

  // Group sub-categories by parent for column display
  const groupedSubCategories = useMemo(() => {
    const activeCategory = categories[activeMenuId.child0];
    if (!activeCategory?.children) return [];

    // Group children by their parent or show as individual columns
    return activeCategory.children.map((child: any) => ({
      title: child.name,
      items: child.children || [],
      seo: child.seo,
      identifier: child.identifier,
    }));
  }, [categories, activeMenuId.child0]);

  return (
    <StyledPaper className="megamenu-wrapper expanded-menu-mega-menu-v2" sx={HeaderMegaMenuSX.wrapper}>
      <StyledGrid container sx={{ ...HeaderMegaMenuSX.container, minHeight: "440px" }}>
        {/* Left Navigation Column - Primary Categories */}
        <StyledGrid
          item
          className="expanded-menu-left-nav"
          md={4}
          sx={{
            ...HeaderMegaMenuSX.leftColumn,
            p: 0,
            paddingRight: { md: "24px !important" },
            m: 0,
            maxWidth: { lg: "365px" },
            flexBasis: { lg: "365px" },
            "@media (min-width:960px) and (max-width:1000px)": {
              width: "357px",
              maxWidth: "357px",
              flexBasis: "357px",
            },

            "@media (min-width: 1000px) and (max-width: 1100px)": {
              width: "365px",
              maxWidth: "365px",
              flexBasis: "365px",
            },

            "@media (min-width:1100px)": {
              width: "365px",
              maxWidth: "365px",
              flexBasis: "365px",
            },
          }}>
          <StyledTypography variant="body2" sx={HeaderMegaMenuSX.leftColumnHeading}>
            UNIVERS
          </StyledTypography>

          {categories && (
            <StyledBox component="ul" sx={HeaderMegaMenuSX.leftColumnItems}>
              {categories?.map((page: any, index: number) => {
                const isActive = index === activeMenuId.child0;
                return (
                  <StyledBox
                    component="li"
                    key={page.id}
                    sx={[
                      HeaderMegaMenuSX.leftColumnMenuListItem,
                      isActive && {
                        backgroundColor: "#FFF6F5",
                        position: "relative",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: "5px",
                          backgroundColor: palette.primary.main,
                          borderRadius: "0px 2px 2px 0px",
                        },
                      },
                    ]}>
                    {page.children ? (
                      <StyledButton
                        fullWidth
                        variant="text"
                        onClick={() => {
                          AsyncCall.sendClick(CLICK.NAV, { navlink: page.name });
                          setActiveMenuId({ child0: index, child1: 0 });
                        }}
                        sx={[HeaderMegaMenuSX.menuListItemButton, { textTransform: "none" }]}>
                        <ImageLayout
                          src={storeUtil.getUrlPicture(`img/contrib/categories/icons/${page?.identifier}.svg`, "")}
                          height={
                            !storeUtil.getUrlPicture(`img/contrib/categories/icons/${page?.identifier}.svg`, "")
                              ? 24
                              : undefined
                          }
                          width={
                            !storeUtil.getUrlPicture(`img/contrib/categories/icons/${page?.identifier}.svg`, "")
                              ? 24
                              : undefined
                          }
                          styles={{ margin: "0px 12px 0px 0px" }}
                        />
                        {page.name}
                      </StyledButton>
                    ) : (
                      <StyledBox sx={{ py: 0.5, px: 2 }}>
                        <Links page={page} />
                      </StyledBox>
                    )}
                  </StyledBox>
                );
              })}

              {/* Bottom Link - Voir les bonnes affaires */}
              <StyledBox component="li">
                <StyledButton
                  variant="text"
                  startIcon={<DiscountTagIcon />}
                  endIcon={
                    <ArrowForwardIcon sx={{ color: palette.primary.main, ml: 0.5, width: "11px", height: "auto" }} />
                  }
                  className="link-hover-animateds"
                  onClick={() => {
                    AsyncCall.sendClick(CLICK.NAV, { navlink: t("Header.Menu.Tracking.SeeGoodDeals") });
                    closeHeaderMenu && closeHeaderMenu();
                  }}
                  sx={HeaderMegaMenuSX.staticLink}>
                  <StyledHeaderFooterLink
                    className="primary"
                    testId={`header-bonne-affaire}`}
                    to={HUBPROMO}
                    state={{
                      breadCrumbTrailEntryView: [
                        {
                          label: "name",
                          value: "id",
                          seo: "seo",
                        },
                      ],
                    }}>
                    <StyledTypography
                      variant="body4"
                      sx={{ fontSize: "14px", fontWeight: "600", lineHeight: "20px", ml: 1 }}>
                      {t("Header.Menu.Actions.SeeGoodDeals")}
                    </StyledTypography>
                  </StyledHeaderFooterLink>
                </StyledButton>
              </StyledBox>
            </StyledBox>
          )}
        </StyledGrid>

        {/* Center Columns - Sub-categories */}
        {activeCategory?.children && groupedSubCategories.length > 0 && (
          <StyledGrid
            item
            className="expanded-menu-center-columns"
            md={4}
            sx={{
              ...HeaderMegaMenuSX.leftColumn,
              p: 0,
              m: 0,
              width: { lg: "348px" },
              maxWidth: { lg: "348px" },
              flexBasis: { lg: "348px" },

              "@media (max-width: 1400px)": {
                width: "48%",
                maxWidth: "48%",
                flexBasis: "48%",
              },

              "@media (max-width: 1340px)": {
                width: "46%",
                maxWidth: "46%",
                flexBasis: "46%",
              },

              "@media (max-width: 1300px)": {
                width: "44%",
                maxWidth: "44%",
                flexBasis: "44%",
              },

              "@media (max-width: 1248px)": {
                width: "43%",
                maxWidth: "43%",
                flexBasis: "43%",
              },

              "@media (max-width: 1220px)": {
                width: "42%",
                maxWidth: "42%",
                flexBasis: "42%",
              },

              "@media (min-width:1401px)": {
                width: "348px",
                maxWidth: "348px",
                flexBasis: "348px",
              },
              px: { md: "20px" },
              py: { md: "30px" },
            }}>
            {/* Category Title and Description */}
            <StyledBox sx={{ mb: 1, flexShrink: 0 }}>
              <StyledTypography variant="h4" sx={{ ...HeaderMegaMenuSX.rightColumnHeading, fontSize: "21px" }}>
                {activeCategory?.name}
              </StyledTypography>
              {/* {activeCategory?.shortDescription && (
                <StyledTypography variant="body1" sx={HeaderMegaMenuSX.rightColumnHeadingDesc}>
                  {activeCategory.shortDescription}
                </StyledTypography>
              )} */}
              {/* PDF Catalog Link */}
              {catalogueLinks.length > 0 && (
                <StyledBox sx={{ mb: "28px" }}>
                  {catalogueLinks.map((link: any, index: number) => (
                    <SmartLink
                      key={index}
                      to={link.url}
                      isExternal={link.type === "external"}
                      sx={HeaderMegaMenuSX.extlink}>
                      <StyledBox component="span" sx={{ ...HeaderMegaMenuSX.extlinkText, fontSize: "14px" }}>
                        {link.label} <ArrowForwardIcon sx={{ ml: 0.5, width: "11px", height: "auto" }} />
                      </StyledBox>
                    </SmartLink>
                  ))}
                </StyledBox>
              )}
            </StyledBox>

            <StyledBox sx={HeaderMegaMenuSX.catalogueItemsWrapper}>
              <StyledGrid container spacing={2}>
                {groupedSubCategories.map((group: any, groupIndex: number) => (
                  <StyledGrid item xs={6} sm={12} md={12} lg={12} key={groupIndex}>
                    <SmartLink
                      key={groupIndex}
                      to={group?.identifier === "SCU1555698" ? MODELSLIST_BIS : group?.seo?.href}
                      onClick={() => closeHeaderMenu && closeHeaderMenu()}>
                      <StyledBox sx={{ marginBottom: "8px" }}>
                        <StyledTypography variant="p" sx={HeaderMegaMenuSX.catalogueItemHeading}>
                          {group.title}
                        </StyledTypography>
                      </StyledBox>
                    </SmartLink>
                  </StyledGrid>
                ))}
              </StyledGrid>
              {/* Bottom Button - Découvrir l'univers */}
              <StyledBox mt={3} pt={3}>
                <StyledButton
                  variant="outlined"
                  className="outlined-bold button-grey"
                  endIcon={<ArrowRightIcon sx={{ color: "#000" }} />}
                  onClick={() => {
                    if (activeCategory?.seo?.href) {
                      AsyncCall.sendClick(CLICK.NAV, {
                        navlink: t("Header.Menu.Tracking.DiscoverUniverse") + "_" + activeCategory.seo.href,
                      });
                      closeHeaderMenu && closeHeaderMenu();
                    }
                  }}
                  sx={HeaderMegaMenuSX.navButtonPrimary}>
                  <StyledHeaderFooterLink
                    to={activeCategory?.seo?.href || "#"}
                    testId={`header-discover-universe-${activeCategory?.id}`}
                    state={{
                      breadCrumbTrailEntryView: [
                        {
                          label: activeCategory?.name || "",
                          value: activeCategory?.id || "",
                          seo: activeCategory?.seo,
                        },
                      ],
                    }}
                    sx={{
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                    {t("Header.Menu.Actions.DiscoverUniverse")} {activeCategory?.name || ""}
                  </StyledHeaderFooterLink>
                </StyledButton>
              </StyledBox>
            </StyledBox>
          </StyledGrid>
        )}

        {/* Right Promotional Banner */}
        <Grid
          item
          xs={12}
          sm={6}
          md={"auto"}
          lg={3}
          sx={{
            display: "flex",
            padding: "10px",
            flexDirection: "column",
            gap: 4,
            flexBasis: { lg: "270px" },
            maxWidth: { lg: "270px" },

            "@media (max-width: 1400px)": {
              width: "270px",
              maxWidth: "270px",
              flexBasis: "270px",
            },

            "@media (min-width:1401px)": {
              width: "270px",
              maxWidth: "270px",
              flexBasis: "270px",
            },
            flexGrow: 0,
            m: 0,
            mt: { xs: 3 },
            p: 0, // padding: 0
            pl: "24px !important", // padding-left: 24px
            pt: "10px !important",
            // "@media (max-width: 1150px)": {
            //   display: "none",
            // },
          }}>
          <BlueCard
            closeHeaderMenu={closeHeaderMenu}
            title="DÉCOUPE SUR-MESURE"
            desc="Lapeyre recoupe vos produits en atelier ou en usine selon votre projet."
            btnLabel="EN SAVOIR PLUS"
            ctaLink={SERVICES_ATELIER}
          />
          <RedCard
            closeHeaderMenu={closeHeaderMenu}
            title="BESOIN DE CONSEIL"
            desc="Nos équipes sont là pour vous conseiller à chaque étape de votre projet."
            btnLabel="PRENDRE RENDEZ-VOUS"
            ctaLink={PRISEDERDV}
          />
        </Grid>
      </StyledGrid>
    </StyledPaper>
  );
};

export default DesktopProductsMenu;
